import React, { useMemo,useEffect } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tooltip } from "primereact/tooltip";
import { Tag } from "primereact/tag";
import type { RouteForExport } from "./ExportRoutesExcel";
import "primeicons/primeicons.css";

export type OptimizedDriver = {
  driverId: number;
  driverName: string;
  orderCount: number;
  totalDistanceKm: number;
  totalTaxableAmount: number;
  vehicleNo?: string;
};

type Props = {
  drivers: OptimizedDriver[];
  selectedDriverId: number | null;
  onChangeDriver: (driverId: number | null) => void;
  onReverseSelected: () => void;
  routesForExport: RouteForExport[];
  onOpenFilter?: () => void;
  className?: string;
  style?: React.CSSProperties;
};

export default function OptimizedRoutesPanel({
  drivers,
  selectedDriverId,
  onChangeDriver,
  onReverseSelected,
  routesForExport,
  onOpenFilter,
  className,
  style,
}: Props) {
  const selectedDriver =
    drivers.find((d) => d.driverId === selectedDriverId) || null;

  const selectedRoute = selectedDriver
    ? routesForExport.find((r) => r.driverName === selectedDriver.driverName)
    : undefined;

  const rows = useMemo(() => {
    if (!selectedRoute?.ordersInRoute?.length) return [];
    return selectedRoute.ordersInRoute
      .slice()
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
      .map((o, idx) => ({ seq: o.sequence ?? idx + 1, orderId: o.orderId }));
  }, [selectedRoute]);

  useEffect(() => {
  if (selectedDriver) {
    console.log(
      `Selected → ${selectedDriver.driverName}: ` +
      `Taxable Total = ₹${selectedDriver.totalTaxableAmount.toFixed(2)}, ` +
      `Distance PK = ${selectedDriver.totalDistanceKm.toFixed(2)} km, ` +
      `Orders = ${selectedDriver.orderCount}`
    );
  }
}, [selectedDriver]);

  const totalOrders = selectedRoute?.ordersInRoute?.length ?? 0;

  return (
    <div
      className={className}
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 12,
        background: "#fff",
        ...style,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <h3 style={{ margin: 0 }}>Optimized Routes</h3>
      </div>

      {/* Driver selector */}
      <div style={{ display: "grid", gap: 6, marginBottom: 8 }}>
        <label className="p-text-secondary" style={{ fontWeight: 600 }}>
          Driver
        </label>
        <div style={{ display: "flex", gap: 6 }}>
        <Dropdown
          value={selectedDriverId}
          onChange={(e) => onChangeDriver(e.value as number)}
          options={drivers.map((d) => ({
            label: `${d.driverName} (${d.orderCount})`,
            value: d.driverId,
          }))}
          placeholder="Select driver"
          style={{ width: "60%" }}
          filter
        />
        <Button
  onClick={onReverseSelected}
  disabled={selectedDriverId == null || totalOrders === 0}
  className="p-button"
  tooltip="Reverse sequence"
  tooltipOptions={{ position: "top" }}
  style={{ width: "30%" }}
>
  <i className="pi pi-map-marker" style={{ marginRight: 8 }} />
  <i className="pi pi-sync" style={{ marginRight: 6 }} />
    
  {/* <span className="p-button-label">Reverse sequence</span> */}
</Button>
</div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        {selectedDriver ? (
          <>
<Tag id="totalOrdersTag" style={{ background:"#f3f4f6", color:"#111827", cursor:"pointer" }}>
  <span style={{ display:"inline-flex", alignItems:"center", gap:8 }}>
    <span style={{ width:22, height:22, borderRadius:999, background:"#e5e7eb",
                   display:"inline-flex", alignItems:"center", justifyContent:"center" }}>
      <i className="pi pi-shopping-cart" style={{ fontSize:13 }} />
    </span>
    <b>| {totalOrders}</b>
  </span>
</Tag>
<Tooltip target="#totalOrdersTag" content={`Number of orders assigned to ${selectedDriver?.driverName ?? "—"}`} position="top" />

<Tag id="totalOrdersamount" style={{ background:"#f3f4f6", color:"#111827", cursor:"pointer" }}>
  <span style={{ display:"inline-flex", alignItems:"center", gap:8 }}>
    <span style={{ width:22, height:22, borderRadius:999, background:"#e5e7eb",
                   display:"inline-flex", alignItems:"center", justifyContent:"center" }}>
      <i className="pi pi-money-bill" style={{ fontSize:13 }} />
    </span>
    <b>|₹ {selectedDriver.totalTaxableAmount.toFixed(2)}</b>
  </span>
</Tag>
<Tooltip target="#totalOrdersamount" content={` Total amount for ${selectedDriver.driverName} `} position="top" />




            <Tag
              value={
                <>
                  <i className="pi pi-map-marker" style={{ marginRight: 4 }} />
                  | {selectedDriver.totalDistanceKm.toFixed(2)} km
                </>
              }
              style={{
                background: "#f3f4f6",
                color: "#111827",
                cursor: "pointer",
              }}
              id="distanceTag"
            />
            <Tooltip
              target="#distanceTag"
              content={`Total distance for ${selectedDriver.driverName}`}
              position="top"
            />
          </>
        ) : (
          <span>No route selected</span>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          {onOpenFilter && (
            <Button
              icon="pi pi-filter"
              label="Filter"
              onClick={onOpenFilter}
              severity="secondary"
              outlined
            />
          )}
        </div>
      </div>

      {/* Table */}
      <DataTable
        value={rows}
        stripedRows
        size="small"
        scrollable
        scrollHeight="420px"
        emptyMessage="No orders to show"
        tableStyle={{ minWidth: 360 }}
      >
        <Column
          field="seq"
          header="Sequence"
          sortable
          style={{ width: 110, textAlign: "center" }}
          body={(row) => (
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{row.seq}</span>
          )}
        />
        <Column
          field="orderId"
          header="Order ID"
          sortable
          body={(row) => (
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{row.orderId}</span>
          )}
        />
        <Column
          header="Driver Name"
          body={() => <span>{selectedDriver?.driverName ?? "—"}</span>}
        />
      </DataTable>
    </div>
  );
}
